/**
 * Shared shell for the HTML document templates (ใบเสร็จ, ใบรับรองแพทย์, …) that
 * replace the Google Docs / Apps Script renderer.
 *
 * Plain JS on purpose — no React, no DOM — so ONE template module renders in two
 * places: the browser (instant preview in an iframe, nothing created) and the
 * server (the same HTML handed to headless Chrome → PDF → Wasabi). What staff see
 * in the preview is byte-for-byte what gets filed.
 *
 * Every value is escaped here. Patient names and addresses are user input; a
 * template must never concatenate a raw value into markup.
 */

export const BRAND = {
  blue: '#17479e',
  blueSoft: '#eaf1fb',
  blueLine: '#c9d9f1',
  orange: '#f39c22',
  ink: '#0f172a',
  muted: '#64748b',
  rule: '#e2e8f0',
}

// Clinic-wide contact line — copied from the existing Google Docs footer.
export const CONTACTS = [
  { icon: 'phone', text: '02-114-3274' },
  { icon: 'mail', text: 'contact@smileseasons.com' },
  { icon: 'web', text: 'www.smileseasons.com' },
  { icon: 'line', text: '@smileseasons.dc' },
]

export function esc(v) {
  return String(v ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

/**
 * Rich text from the dialog's RichTextEditor → markup safe to print. The editor
 * already sanitizes, but this runs on the SERVER too, where the payload is
 * whatever the request sent — so it is an allowlist, not a trust:
 *   - only b/strong/i/em/u/ul/ol/li/p/br survive (div → p, span dropped),
 *     every attribute stripped;
 *   - tags are re-balanced, so an unclosed <b> cannot leak bold into the rest
 *     of the document;
 *   - text is passed through as the editor's entity-encoded text; a stray "<"
 *     is escaped. Plain text (no tags) keeps its line breaks.
 */
const RICH_TAGS = { b: 'b', strong: 'b', i: 'i', em: 'i', u: 'u', ul: 'ul', ol: 'ol', li: 'li', p: 'p', div: 'p' }
export function richText(html) {
  const src = String(html ?? '').replace(/<(script|style|template)\b[\s\S]*?(<\/\1\s*>|$)/gi, '')
  if (!/<[a-z/!]/i.test(src)) return esc(src).replace(/\r?\n/g, '<br>')
  const out = []
  const stack = []
  const re = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>|<!--[\s\S]*?-->|<|[^<]+/g
  let m
  while ((m = re.exec(src)) !== null) {
    const tok = m[0]
    if (m[2] === undefined) {
      if (tok === '<') out.push('&lt;')
      else if (!tok.startsWith('<!--')) out.push(tok.replace(/>/g, '&gt;').replace(/&(?![a-zA-Z]+;|#\d+;|#x[0-9a-fA-F]+;)/g, '&amp;'))
      continue
    }
    const name = m[2].toLowerCase()
    if (name === 'br') { if (!m[1]) out.push('<br>'); continue }
    const tag = RICH_TAGS[name]
    if (!tag) continue
    if (!m[1]) { stack.push(tag); out.push(`<${tag}>`); continue }
    const at = stack.lastIndexOf(tag)
    if (at < 0) continue
    while (stack.length > at) out.push(`</${stack.pop()}>`)
  }
  while (stack.length) out.push(`</${stack.pop()}>`)
  return out.join('')
}

// Small stroke icons (Tabler shapes, inlined so the PDF has no external fetch).
const ICON_PATHS = {
  phone: '<path d="M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5 -2.5l5 2v4a2 2 0 0 1 -2 2a16 16 0 0 1 -15 -15a2 2 0 0 1 2 -2"/>',
  mail: '<path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z"/><path d="M3 7l9 6l9 -6"/>',
  web: '<path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"/><path d="M3.6 9h16.8"/><path d="M3.6 15h16.8"/><path d="M11.5 3a17 17 0 0 0 0 18"/><path d="M12.5 3a17 17 0 0 1 0 18"/>',
  calendar: '<path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z"/><path d="M16 3v4"/><path d="M8 3v4"/><path d="M4 11h16"/>',
}

// Brand marks drawn as solid shapes rather than strokes — the LINE logo (its
// letters are knocked out of the bubble, so it must be filled to read).
const FILLED_ICONS = {
  line: { viewBox: '0 0 296.528 296.528', path: '<path d="M295.838,115.347l0.003-0.001l-0.092-0.76c-0.001-0.013-0.002-0.023-0.004-0.036c-0.001-0.011-0.002-0.021-0.004-0.032l-0.344-2.858c-0.069-0.574-0.148-1.228-0.238-1.974l-0.072-0.594l-0.147,0.018c-3.617-20.571-13.553-40.093-28.942-56.762c-15.317-16.589-35.217-29.687-57.548-37.878c-19.133-7.018-39.434-10.577-60.337-10.577c-28.22,0-55.627,6.637-79.257,19.193C23.289,47.297-3.585,91.799,0.387,136.461c2.056,23.111,11.11,45.11,26.184,63.621c14.188,17.423,33.381,31.483,55.503,40.66c13.602,5.642,27.051,8.301,41.291,11.116l1.667,0.33c3.921,0.776,4.975,1.842,5.247,2.264c0.503,0.784,0.24,2.329,0.038,3.18c-0.186,0.785-0.378,1.568-0.57,2.352c-1.529,6.235-3.11,12.683-1.868,19.792c1.428,8.172,6.531,12.859,14.001,12.86c0.001,0,0.001,0,0.002,0c8.035,0,17.18-5.39,23.231-8.956l0.808-0.475c14.436-8.478,28.036-18.041,38.271-25.425c22.397-16.159,47.783-34.475,66.815-58.17C290.172,175.745,299.2,145.078,295.838,115.347z M92.343,160.561H66.761c-3.866,0-7-3.134-7-7V99.865c0-3.866,3.134-7,7-7c3.866,0,7,3.134,7,7v46.696h18.581c3.866,0,7,3.134,7,7C99.343,157.427,96.209,160.561,92.343,160.561z M119.03,153.371c0,3.866-3.134,7-7,7c-3.866,0-7-3.134-7-7V99.675c0-3.866,3.134-7,7-7c3.866,0,7,3.134,7,7V153.371z M182.304,153.371c0,3.033-1.953,5.721-4.838,6.658c-0.712,0.231-1.441,0.343-2.161,0.343c-2.199,0-4.323-1.039-5.666-2.888l-25.207-34.717v30.605c0,3.866-3.134,7-7,7c-3.866,0-7-3.134-7-7v-52.16c0-3.033,1.953-5.721,4.838-6.658c2.886-0.936,6.045,0.09,7.827,2.545l25.207,34.717V99.675c0-3.866,3.134-7,7-7c3.866,0,7,3.134,7,7V153.371z M233.311,159.269h-34.645c-3.866,0-7-3.134-7-7v-26.847V98.573c0-3.866,3.134-7,7-7h33.57c3.866,0,7,3.134,7,7s-3.134,7-7,7h-26.57v12.849h21.562c3.866,0,7,3.134,7,7c0,3.866-3.134,7-7,7h-21.562v12.847h27.645c3.866,0,7,3.134,7,7S237.177,159.269,233.311,159.269z"/>' },
}

export function icon(name, size = 11) {
  const f = FILLED_ICONS[name]
  if (f) return `<svg class="ic" width="${size}" height="${size}" viewBox="${f.viewBox}" fill="currentColor">${f.path}</svg>`
  return `<svg class="ic" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${ICON_PATHS[name] || ''}</svg>`
}

/**
 * The document typefaces — IBM Plex Sans Thai (headings, figures) with its
 * Looped sibling for body text: one design family, so the pair agrees; looped
 * Thai reads better at receipt sizes; Plex figures tabulate cleanly. SIL OFL
 * (public/doc/fonts/OFL.txt), so we ship the files and a render never waits on
 * Google Fonts.
 *
 * `src(file)` maps a file name to a URL: a same-origin path in the browser
 * preview, a data: URI on the server so headless Chrome fetches nothing.
 */
export const FONT_FILES = [
  { family: 'Plex Thai', weight: 600, file: 'IBMPlexSansThai-SemiBold.ttf' },
  { family: 'Plex Thai Looped', weight: 400, file: 'IBMPlexSansThaiLooped-Regular.ttf' },
  { family: 'Plex Thai Looped', weight: 600, file: 'IBMPlexSansThaiLooped-SemiBold.ttf' },
]

/**
 * Styles every document shares: the title + logo header, the two-cell meta
 * strip under it, and the two-column parties block (patient | clinic).
 */
export const HEADER_CSS = `
.top { display: flex; justify-content: space-between; align-items: flex-start; }
.top h1 { margin: 0; font-size: 24pt; line-height: 1.15; color: ${BRAND.ink}; }
.top .sub { margin-top: 1mm; font-size: 10pt; color: ${BRAND.muted}; letter-spacing: .04em; }
.logo { height: 13mm; width: auto; margin-top: 1mm; }

.meta { display: flex; margin: 7mm 0 6mm; border-top: 2px solid ${BRAND.blue}; border-bottom: 1px solid ${BRAND.rule}; }
.meta > div { flex: 1; padding: 2.4mm 0 2.6mm; display: flex; flex-direction: column; }
.meta > div + div { padding-left: 5mm; border-left: 1px solid ${BRAND.rule}; }
.meta span { font-size: 8.5pt; color: ${BRAND.muted}; }
.meta b { font-weight: 600; font-size: 10.5pt; }

.parties { display: grid; grid-template-columns: 1fr 1fr; gap: 8mm; margin-bottom: 7mm; }
.party .label, .next .label { font-size: 8.5pt; font-weight: 600; color: ${BRAND.blue}; margin-bottom: 1.2mm; }
.party .name { font-family: 'Plex Thai', sans-serif; font-weight: 600; font-size: 12pt; line-height: 1.3; margin-bottom: 1mm; }
.party .plain { font-size: 9.5pt; color: #334155; margin-bottom: .8mm; }
.party .kv { font-size: 9.5pt; color: #334155; }
.party .kv > span:first-child { color: ${BRAND.muted}; margin-right: 1.5mm; }
`

/**
 * The tiny trace line on the footer's last line — the treatment(s) a document
 * came from and when it was printed. Both stamped into the payload by the
 * builder, so the preview and the filed PDF agree.
 */
export function traceLine(d, lang = 'th') {
  const en = lang === 'en'
  const at = (en && d.printed_atEN) || d.printed_at
  return [
    d.treatment_ids ? `Treatment ID ${d.treatment_ids}` : '',
    at ? `${en ? 'Printed' : 'พิมพ์เมื่อ'} ${at}` : '',
  ].filter(Boolean).join(' · ')
}

export function fontFaceCss(src) {
  return `<style>${FONT_FILES.map((f) =>
    `@font-face{font-family:'${f.family}';font-weight:${f.weight};font-style:normal;font-display:block;src:url('${src(f.file)}') format('truetype')}`,
  ).join('')}</style>`
}

/**
 * Wrap a template body in the A4 page shell.
 *   title    — <title>, also the PDF's document title
 *   body     — the template's markup
 *   footer   — branch line shown in the running footer
 *   meta     — tiny trace text on the footer's last line (treatment ids, print time)
 *   assets   — { logo, fontCss } — the logo URL (a data: URI on the server, so
 *              the renderer fetches nothing) and fontFaceCss(…) output
 *   css      — the template's own styles
 */
export function page({ title, body, footer = '', meta = '', assets = {}, css = '', lang = 'th' }) {
  return `<!doctype html>
<html lang="${lang}">
<head>
<meta charset="utf-8">
<title>${esc(title)}</title>
${assets.fontCss || ''}
<style>
@page { size: A4; margin: 11mm 16mm 9mm; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; background: #fff; }
body {
  font-family: 'Plex Thai Looped', 'Noto Sans Thai Looped', sans-serif;
  font-size: 10.5pt; line-height: 1.5; color: ${BRAND.ink};
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
}
.sheet { position: relative; }
h1, .display { font-family: 'Plex Thai', sans-serif; font-weight: 600; }
.ic { display: inline-block; vertical-align: -1.5px; }
.num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.muted { color: ${BRAND.muted}; }

/* Running footer. position:fixed repeats it at the bottom of every printed
   page; the layout table's <tfoot> spacer (also repeated per page) reserves its
   height, so body text on page 2+ never runs underneath it. Chrome cannot put
   fixed content in the page MARGIN — it spills to a new page — hence this. */
.foot {
  position: fixed; left: 0; right: 0; bottom: 0; height: 17mm;
  border-top: 1px solid ${BRAND.rule}; padding-top: 2.5mm;
  font-size: 8.5pt; color: ${BRAND.muted};
}
.foot b { display: block; color: ${BRAND.ink}; font-weight: 600; margin-bottom: .8mm; }
.foot .foot-meta { margin-top: .8mm; font-size: 6.5pt; color: #94a3b8; white-space: nowrap; font-variant-numeric: tabular-nums; }
.foot-space { height: 21mm; }
table.layout { width: 100%; border-collapse: collapse; }
table.layout > tbody > tr > td, table.layout > tfoot > tr > td { padding: 0; }
/* 3mm of the top margin moved INSIDE the cell: printing clips the table's first
   fragment at the cell's top edge, which cut off Thai tone marks stacked above a
   vowel in the title (ใบแจ้งหนี้ printed as ใบแจ้งหนี). */
table.layout > tbody > tr > td { padding-top: 3mm; }
table.layout > tfoot { display: table-footer-group; }
.foot .contacts { display: flex; gap: 5mm; white-space: nowrap; }
.foot .contacts span { display: inline-flex; align-items: center; gap: 1.2mm; }
.foot .ic { color: ${BRAND.blue}; }
/* On screen (the instant preview) draw the sheet the way print lays it out:
   the footer pinned to the sheet's bottom instead of every page's. */
@media screen {
  body { background: #e2e8f0; padding: 24px 0; }
  .sheet { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 11mm 16mm 29mm; background: #fff; box-shadow: 0 1px 3px rgb(0 0 0 / .12); }
  .foot { position: absolute; left: 16mm; right: 16mm; bottom: 9mm; }
  .foot-space { display: none; }
}
${css}
</style>
</head>
<body>
<div class="sheet">
<div class="foot">
  <b>${esc(footer)}</b>
  <div class="contacts">${CONTACTS.map((c) => `<span>${icon(c.icon)}${esc(c.text)}</span>`).join('')}</div>
  ${meta ? `<div class="foot-meta">${esc(meta)}</div>` : ''}
</div>
<table class="layout">
<tbody><tr><td>
${body}
</td></tr></tbody>
<tfoot><tr><td><div class="foot-space"></div></td></tr></tfoot>
</table>
</div>
</body>
</html>`
}
